import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {KeyRound} from 'lucide-react';
import {usePath, useIVComputedDetails, useRemoteAccessEnabled} from 'hooks';
import {ANALYTIC_EVENTS, Analytics, useIVStore} from '@chekin/shared';
import {useGetGuestId} from '../../../hooks/useGetGuestId';
import {PATHS} from 'routes/constants';
import {Button, InfoBox, ResponsiveSheet, useModalControls} from '@chekinapp/ui';
import {SheetActions} from 'components/common/SheetActions';

export type SkipIVModalRef = {
  open: (link?: string) => void;
};

const SkipIVModal = React.forwardRef<SkipIVModalRef, unknown>((_, ref) => {
  const {t} = useTranslation();
  const {
    isOpen: isOpenSkipVerificationModal,
    closeModal: closeSkipModal,
    openModal: openSkipModal,
  } = useModalControls();
  const guestId = useGetGuestId();
  const navigate = useNavigate();
  const {skipIV} = useIVStore();
  const {required} = useIVComputedDetails();
  const isRemoteAccessEnabled = useRemoteAccessEnabled();
  const hasProgress = useIVStore(state =>
    Object.values(state.imagesDetails).some(details => details?.uploaded),
  );

  const {getMainPath} = usePath();

  const isSkippingRef = React.useRef(false);

  React.useEffect(
    () => () => {
      if (isSkippingRef.current) skipIV();
    },
    [skipIV],
  );

  const onSkippedModal = React.useCallback(() => {
    const pathIdOrEmptyString = guestId || '';
    isSkippingRef.current = true;
    Analytics.track(ANALYTIC_EVENTS.ivSkipping);
    navigate(`${getMainPath(PATHS.addPersonalDataForm)}/${pathIdOrEmptyString}`);
  }, [navigate, getMainPath, guestId]);

  React.useImperativeHandle(
    ref,
    () => ({
      open: openSkipModal,
    }),
    [openSkipModal],
  );

  return (
    <ResponsiveSheet
      open={isOpenSkipVerificationModal}
      onClose={closeSkipModal}
      title={t(required ? 'skip_verification_question' : 'verify_later_question')}
      description={
        <>
          <Trans
            i18nKey={required ? 'skip_iv_checkin_incomplete' : 'skip_iv_optional_intro'}
            components={{1: <b />}}
          />
          {hasProgress && <> {t('skip_iv_progress_lost')}</>}
        </>
      }
      className="skip-iv-modal"
      closeOnOverlayClick
      dialogClassName="sm:max-w-[440px]"
    >
      <div className="mt-4 flex flex-col gap-5">
        {isRemoteAccessEnabled && (
          <InfoBox icon={<KeyRound />}>
            <Trans i18nKey="skip_iv_keys_stay_locked" components={{1: <b />}} />
          </InfoBox>
        )}

        <SheetActions className="gap-2.5">
          {required ? (
            <>
              <Button variant="outline" onClick={onSkippedModal}>
                {t('skip')}
              </Button>
              <Button onClick={closeSkipModal}>{t('continue_verification')}</Button>
            </>
          ) : (
            <>
              <Button onClick={closeSkipModal}>{t('continue_verification')}</Button>
              <Button variant="outline" onClick={onSkippedModal}>
                {t('skip')}
              </Button>
            </>
          )}
        </SheetActions>
      </div>
    </ResponsiveSheet>
  );
});

export {SkipIVModal};
